import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  HeartHandshake,
  Stethoscope,
  Hospital,
  Pill,
  Layers,
  UserCheck,
  CalendarClock,
  CheckCircle2,
  Info,
  ArrowRight,
  Landmark
} from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/7dfacbbb-8e0d-4602-a3aa-7ff35c27b573/images/7a32714280eae6ac1c7e695ba40ae651338897378f84f30462fb5622dc2ef5b6.png";
const SUPPORT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/7dfacbbb-8e0d-4602-a3aa-7ff35c27b573/images/c23a18fa779c21843d58bf9b9c7f35508a78111dd93e3c46dea9f7d37bab7504.png";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const partIcons = [Hospital, Stethoscope, Layers, Pill, ShieldCheck];

export const MedicarePage = () => {
  const { t } = useTranslation();

  const parts = t('medicare.parts', { returnObjects: true });
  const qualifyItems = t('medicare.qualifyItems', { returnObjects: true });
  const periods = t('medicare.periods', { returnObjects: true });
  const helpItems = t('medicare.helpItems', { returnObjects: true });
  const disclaimers = t('medicare.disclaimers', { returnObjects: true });

  return (
    <div className="overflow-hidden" data-testid="medicare-page">
      {/* Hero Section */}
      <section className="gradient-hero py-20 lg:py-28" data-testid="medicare-hero">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-6">
                <HeartHandshake className="h-4 w-4" />
                {t('medicare.heroBadge')}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                {t('medicare.heroTitle')}{' '}
                <span className="text-gradient">{t('medicare.heroTitleHighlight')}</span>
              </h1>

              <p className="text-lg text-slate-600 mb-8">
                {t('medicare.heroDescription')}
              </p>

              <Link to="/contact">
                <Button className="btn-accent" data-testid="medicare-hero-cta">
                  {t('medicare.heroCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src={HERO_IMAGE}
                alt="Active senior couple enjoying time outdoors"
                className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
                data-testid="medicare-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro / How we support */}
      <section className="section-spacing bg-white" data-testid="medicare-intro">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={SUPPORT_IMAGE}
                alt="Licensed advisor explaining Medicare options to a senior"
                className="rounded-2xl shadow-xl"
                data-testid="medicare-support-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
                <UserCheck className="h-4 w-4" />
                {t('medicare.introTitle')}
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('medicare.introTitle')}
              </h2>

              <p className="text-lg text-slate-600">
                {t('medicare.introText')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parts of Medicare */}
      <section className="section-spacing gradient-subtle" data-testid="medicare-parts">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('medicare.partsTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('medicare.partsSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parts.map((part, index) => {
              const Icon = partIcons[index] || ShieldCheck;
              const isPrivate = index >= 2;
              return (
                <motion.div
                  key={part.tag}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow flex flex-col"
                  data-testid={`medicare-part-${index}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${isPrivate ? 'bg-emerald-500' : 'bg-slate-900'}`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{part.tag}</span>
                      <h3 className="text-lg font-semibold text-slate-900 leading-tight">{part.name}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1">{part.desc}</p>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium w-fit ${isPrivate ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {isPrivate ? <HeartHandshake className="h-3 w-3" /> : <Landmark className="h-3 w-3" />}
                    {isPrivate ? t('medicare.privatePill') : t('medicare.federalPill')}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Qualifies */}
      <section className="section-spacing bg-white" data-testid="medicare-qualify">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 rounded-full text-sm font-medium mb-6">
                <UserCheck className="h-4 w-4" />
                {t('medicare.qualifyTitle')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('medicare.qualifyTitle')}
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                {t('medicare.qualifySubtitle')}
              </p>

              <div className="space-y-3">
                {qualifyItems.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900 rounded-3xl p-10 lg:p-12"
            >
              <Stethoscope className="h-12 w-12 text-emerald-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">{t('medicare.helpTitle')}</h3>
              <p className="text-slate-300 mb-8">{t('medicare.helpText')}</p>
              <div className="space-y-3">
                {helpItems.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-200 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enrollment Periods */}
      <section className="section-spacing gradient-subtle" data-testid="medicare-periods">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <CalendarClock className="h-4 w-4" />
              {t('medicare.periodsTitle')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('medicare.periodsTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('medicare.periodsSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {periods.map((period, index) => (
              <motion.div
                key={period.abbr}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-slate-100 card-shadow"
                data-testid={`medicare-period-${index}`}
              >
                <span className="inline-block px-3 py-1 bg-slate-900 text-white rounded-full text-xs font-semibold mb-4">
                  {period.abbr}
                </span>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{period.name}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{period.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimers */}
      <section className="py-16 bg-white border-t border-slate-100" data-testid="medicare-disclaimers">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-slate-50 border border-slate-100 rounded-2xl p-8"
          >
            <div className="flex items-center gap-2 mb-5">
              <Info className="h-5 w-5 text-sky-500" />
              <h3 className="text-lg font-semibold text-slate-900">{t('medicare.disclaimerTitle')}</h3>
            </div>
            <ul className="space-y-3">
              {disclaimers.map((item, index) => (
                <li key={index} className="flex items-start gap-3" data-testid={`medicare-disclaimer-${index}`}>
                  <ShieldCheck className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                  <span className="text-slate-600 text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900" data-testid="medicare-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('medicare.ctaTitle')}
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
              {t('medicare.ctaText')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-8 rounded-full font-semibold" data-testid="medicare-cta-btn">
                  {t('medicare.ctaButton')}
                </Button>
              </Link>
              <Link to="/health-plans">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 rounded-full" data-testid="medicare-cta-health-btn">
                  {t('medicare.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MedicarePage;
